/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from "react";
import ParentSize from "@visx/responsive/lib/components/ParentSize";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Group } from "@visx/group";
import { AxisLeft, AxisRight, AxisBottom } from "@visx/axis";
import { LinePath } from "@visx/shape";
import { curveLinear } from "@visx/curve";
import { ClearTherapySessionDetail } from "../../services/graphs";

// ── Colors (match ClearTherapyPressureGraph) ─────────────────────────────────
const COLORS = {
  neb: "#00BCD4",
  pep: "#FF6B6B",
  osc: "#1565C0",
  grid: "#f0f0f0",
  cursor: "#1565C0",
  pepWithNeb: "#00BCD4", // NEB color for PEP w/ NEB dots
};
const PRESSURE_PEP_COLOR = "#FF6B6B";
const PRESSURE_OSC_COLOR = "#4FC3F7";

const PEP_NEB_DOT_ID = "day-sessions-pep-neb-dots";

// ── Helpers ───────────────────────────────────────────────────────────────────
/** Extract HH:MM from a therapy start-time string, fallback to "S{n}" */
const sessionLabel = (s: ClearTherapySessionDetail, idx: number): string => {
  if (s.startTime) {
    const m = s.startTime.match(/(\d{1,2}:\d{2})/);
    if (m) return m[1];
  }
  return `S${idx + 1}`;
};

// ── Inner chart ───────────────────────────────────────────────────────────────
interface InnerProps {
  sessions: ClearTherapySessionDetail[];
  labels: string[];
  width: number;
  height: number;
  activeRefId?: string | null;
  onSessionClick?: (refId: string) => void;
}

const ClearDaySessionsChartInner: React.FC<InnerProps> = ({
  sessions,
  labels,
  width,
  height,
  activeRefId,
  onSessionClick,
}) => {
  const margin = { top: 20, right: 55, bottom: 50, left: 50 };
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  // ── Scales ────────────────────────────────────────────────────────────────
  const xScale = useMemo(() =>
    scaleBand({ domain: labels, range: [0, xMax], padding: 0.28 }),
  [labels, xMax]);

  const maxStack = useMemo(() =>
    Math.max(5, Math.ceil(
      Math.max(...sessions.map(s => s.pepDuration + s.oscDuration + s.nebDuration)) * 1.25 / 5
    ) * 5),
  [sessions]);

  const yDuration = useMemo(() =>
    scaleLinear({ domain: [0, maxStack], range: [yMax, 0] }),
  [maxStack, yMax]);

  const maxPressure = useMemo(() => {
    const vals = sessions.flatMap(s => [
      s.pepPressure?.median ?? 0,
      s.oscPressure?.median ?? 0,
    ]).filter(v => v > 0);
    return vals.length ? Math.ceil(Math.max(...vals) * 1.25 / 5) * 5 : 30;
  }, [sessions]);

  const yPressure = useMemo(() =>
    scaleLinear({ domain: [0, maxPressure], range: [yMax, 0], nice: true }),
  [maxPressure, yMax]);

  // ── Tick helpers ─────────────────────────────────────────────────────────
  const durationTicks = useMemo(() => {
    const step = maxStack <= 10 ? 1 : maxStack <= 30 ? 5 : 10;
    return Array.from({ length: Math.floor(maxStack / step) + 1 }, (_, i) => i * step);
  }, [maxStack]);

  const pressureTicks = yPressure.ticks(5).map(t => Math.round(t));

  // ── Pressure line data ───────────────────────────────────────────────────
  const pepLine = useMemo(() =>
    sessions
      .map((s, i) => s.pepPressure ? { x: labels[i], y: s.pepPressure.median } : null)
      .filter((p): p is { x: string; y: number } => p !== null),
  [sessions, labels]);

  const oscLine = useMemo(() =>
    sessions
      .map((s, i) => s.oscPressure ? { x: labels[i], y: s.oscPressure.median } : null)
      .filter((p): p is { x: string; y: number } => p !== null),
  [sessions, labels]);

  const diamond = (cx: number, cy: number, size: number) =>
    `M ${cx},${cy - size} L ${cx + size},${cy} L ${cx},${cy + size} L ${cx - size},${cy} Z`;

  return (
    <svg width={width} height={height} style={{ overflow: "visible" }}>
      <defs>
        {/* PEP w/ NEB dot pattern */}
        <pattern id={PEP_NEB_DOT_ID} patternUnits="userSpaceOnUse" width="6" height="6">
          <rect width="6" height="6" fill="transparent" />
          <circle cx="3" cy="3" r="1" fill={COLORS.pepWithNeb} opacity="0.75" />
        </pattern>
      </defs>

      <Group left={margin.left} top={margin.top}>
        {/* Grid */}
        {yDuration.ticks(5).map((tick, i) => (
          <line key={i} x1={0} y1={yDuration(tick)} x2={xMax} y2={yDuration(tick)}
            stroke={COLORS.grid} strokeWidth={1} />
        ))}

        {/* Stacked bars: PEP (bottom) → OSC (middle) → NEB (top) */}
        {sessions.map((s, idx) => {
          const label    = labels[idx];
          const bx       = xScale(label) ?? 0;
          const bw       = xScale.bandwidth();
          const barW     = bw * 0.72;
          const barX     = bx + (bw - barW) / 2;
          const isActive = activeRefId === s.refId;

          // PEP — bottom layer
          const pepY = yDuration(s.pepDuration);
          const pepH = yMax - pepY;

          // OSC — middle layer (sits on top of PEP)
          const oscY = yDuration(s.pepDuration + s.oscDuration);
          const oscH = yDuration(s.pepDuration) - oscY;

          // NEB — top layer (sits on top of OSC)
          const nebY = yDuration(s.pepDuration + s.oscDuration + s.nebDuration);
          const nebH = yDuration(s.pepDuration + s.oscDuration) - nebY;

          // PEP w/ NEB dot overlay — bottom portion of PEP bar
          const pepNebDuration = Math.min(s.pepWithNebTime ?? 0, s.pepDuration);
          const pepNebH = yMax - yDuration(pepNebDuration); // height from bottom of PEP

          return (
            <g
              key={s.refId}
              onClick={() => onSessionClick?.(s.refId)}
              style={{ cursor: onSessionClick ? "pointer" : "default" }}
            >
              {/* Active column highlight */}
              {isActive && (
                <rect x={bx - 1} y={0} width={bw + 2} height={yMax}
                  fill="rgba(0,163,224,0.07)" rx={3} />
              )}

              {/* PEP (bottom) */}
              {s.pepDuration > 0 && (
                <rect x={barX} y={pepY} width={barW} height={Math.max(pepH, 0)}
                  fill={COLORS.pep}
                  rx={s.oscDuration === 0 && s.nebDuration === 0 ? 2 : 0} />
              )}

              {/* PEP w/ NEB — blue dot overlay on bottom portion of PEP */}
              {pepNebDuration > 0 && s.pepDuration > 0 && (
                <rect
                  x={barX}
                  y={yDuration(pepNebDuration)}
                  width={barW}
                  height={Math.max(pepNebH, 0)}
                  fill={`url(#${PEP_NEB_DOT_ID})`}
                  rx={0}
                  pointerEvents="none"
                />
              )}

              {/* OSC (middle) */}
              {s.oscDuration > 0 && (
                <rect x={barX} y={oscY} width={barW} height={Math.max(oscH, 0)}
                  fill={COLORS.osc}
                  rx={s.nebDuration === 0 ? 2 : 0} />
              )}

              {/* NEB (top, solid) */}
              {s.nebDuration > 0 && (
                <rect x={barX} y={nebY} width={barW} height={Math.max(nebH, 0)}
                  fill={COLORS.neb} rx={2} />
              )}

              {/* Active cursor */}
              {isActive && (
                <line x1={bx + bw / 2} y1={0} x2={bx + bw / 2} y2={yMax}
                  stroke={COLORS.cursor} strokeWidth={2} strokeDasharray="4,4"
                  pointerEvents="none" />
              )}
            </g>
          );
        })}

        {/* Pressure lines (connecting session medians) */}
        {pepLine.length > 1 && (
          <LinePath
            data={pepLine}
            x={d => (xScale(d.x) ?? 0) + xScale.bandwidth() / 2}
            y={d => yPressure(d.y)}
            stroke={PRESSURE_PEP_COLOR}
            strokeWidth={1.5}
            strokeDasharray="6,4"
            curve={curveLinear}
          />
        )}
        {oscLine.length > 1 && (
          <LinePath
            data={oscLine}
            x={d => (xScale(d.x) ?? 0) + xScale.bandwidth() / 2}
            y={d => yPressure(d.y)}
            stroke={PRESSURE_OSC_COLOR}
            strokeWidth={1.5}
            strokeDasharray="6,4"
            curve={curveLinear}
          />
        )}

        {/* Pressure median diamonds per session */}
        {sessions.map((s, idx) => {
          const cx = (xScale(labels[idx]) ?? 0) + xScale.bandwidth() / 2;
          return (
            <g key={`pres-${s.refId}`} pointerEvents="none">
              {s.pepPressure && (
                <path d={diamond(cx, yPressure(s.pepPressure.median), 5)}
                  fill={PRESSURE_PEP_COLOR} stroke="white" strokeWidth={1} />
              )}
              {s.oscPressure && (
                <path d={diamond(cx, yPressure(s.oscPressure.median), 5)}
                  fill={PRESSURE_OSC_COLOR} stroke="white" strokeWidth={1} />
              )}
            </g>
          );
        })}

        {/* Axes */}
        <AxisBottom
          top={yMax}
          scale={xScale}
          stroke="#9ca3af"
          tickStroke="#9ca3af"
          tickLength={0}
          tickLabelProps={() => ({ fill: "#666", fontSize: 10, textAnchor: "middle", dy: 4 })}
          label="Session"
          labelProps={{ fill: "#666", fontSize: 11, textAnchor: "middle", dy: 36 }}
        />
        <AxisLeft
          scale={yDuration}
          tickValues={durationTicks}
          stroke="#9ca3af"
          tickStroke="#9ca3af"
          tickLength={0}
          tickLabelProps={() => ({ fill: "#666", fontSize: 10, textAnchor: "end", dx: -6 })}
        />
        <AxisRight
          left={xMax}
          scale={yPressure}
          tickValues={pressureTicks}
          tickFormat={v => String(Math.round(Number(v)))}
          stroke={PRESSURE_OSC_COLOR}
          tickStroke={PRESSURE_OSC_COLOR}
          tickLength={0}
          tickLabelProps={() => ({ fill: PRESSURE_OSC_COLOR, fontSize: 10, textAnchor: "start", dx: 8 })}
        />
      </Group>
    </svg>
  );
};

// ── Public component ──────────────────────────────────────────────────────────
interface ClearDaySessionsChartProps {
  sessions: ClearTherapySessionDetail[];
  /** refId of the currently highlighted session card */
  activeRefId?: string | null;
  /** Called when a session bar is tapped — use to expand/collapse the card below */
  onSessionClick?: (refId: string) => void;
}

const ClearDaySessionsChart: React.FC<ClearDaySessionsChartProps> = ({
  sessions,
  activeRefId,
  onSessionClick,
}) => {
  const labels = useMemo(() => sessions.map(sessionLabel), [sessions]);

  if (sessions.length === 0) {
    return (
      <div className="flex items-center justify-center h-[160px] text-gray-400 text-sm">
        No session data for this day.
      </div>
    );
  }

  return (
    <div>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs px-1 mb-1">
        {/* Bar type legends — in stack order: PEP, OSC, NEB */}
        <span className="flex items-center gap-1">
          <svg width="13" height="13" viewBox="0 0 13 13">
            <rect x="1" y="1" width="11" height="11" rx="2" fill={COLORS.pep} />
          </svg>
          PEP Duration
        </span>
        <span className="flex items-center gap-1">
          <svg width="13" height="13" viewBox="0 0 13 13">
            <rect x="1" y="1" width="11" height="11" rx="2" fill={COLORS.osc} />
          </svg>
          OSC Duration
        </span>
        <span className="flex items-center gap-1">
          <svg width="13" height="13" viewBox="0 0 13 13">
            <rect x="1" y="1" width="11" height="11" rx="2" fill={COLORS.neb} />
          </svg>
          NEB
        </span>

        {/* PEP w/ NEB — blue dot overlay */}
        <span className="flex items-center gap-1">
          <svg width="13" height="13" viewBox="0 0 13 13">
            <rect x="1" y="1" width="11" height="11" rx="2" fill={COLORS.pep} />
            <defs>
              <pattern id="lg-pep-neb" patternUnits="userSpaceOnUse" width="4" height="4">
                <rect width="4" height="4" fill="transparent" />
                <circle cx="2" cy="2" r="0.7" fill={COLORS.pepWithNeb} opacity="0.8" />
              </pattern>
            </defs>
            <rect x="1" y="1" width="11" height="11" rx="2" fill="url(#lg-pep-neb)" />
          </svg>
          PEP w/ NEB
        </span>

        {/* Pressure line legends */}
        {[
          { label: "PEP Pressure", color: PRESSURE_PEP_COLOR },
          { label: "OSC Pressure", color: PRESSURE_OSC_COLOR },
        ].map(item => (
          <span key={item.label} className="flex items-center gap-1">
            <svg width="20" height="13" viewBox="0 0 20 13">
              <line x1="2" y1="6" x2="18" y2="6" stroke={item.color} strokeWidth="1.5" strokeDasharray="4,3" />
              <path d="M 10,3 L 13,6 L 10,9 L 7,6 Z" fill={item.color} />
            </svg>
            {item.label}
          </span>
        ))}

        <span className="text-gray-400 ml-auto text-[10px]">Tap bar to expand session ↓</span>
      </div>

      {/* Chart */}
      <ParentSize>
        {({ width }) =>
          width > 0 ? (
            <ClearDaySessionsChartInner
              sessions={sessions}
              labels={labels}
              width={width}
              height={220}
              activeRefId={activeRefId}
              onSessionClick={onSessionClick}
            />
          ) : null
        }
      </ParentSize>

      {/* Axis unit labels */}
      <div className="flex justify-between px-1 mt-0.5">
        <span className="text-[10px] text-gray-400">Duration (min)</span>
        <span className="text-[10px]" style={{ color: PRESSURE_OSC_COLOR }}>Pressure (cm H₂O)</span>
      </div>
    </div>
  );
};

export default ClearDaySessionsChart;
